import React, { useState } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  Home,
  Package,
  User,
  LogOut,
  X,
  ChevronLeft,
  ChevronRight,
  TrendingUp,
} from 'lucide-react';
import { logout } from '../../store/slices/authSlice';
import DiasporawiseLogo from '../../assets/DWBRANDprview.png';

interface DealerSidebarProps {
  isOpen: boolean;
  onToggle: () => void;
}

const DealerSidebar: React.FC<DealerSidebarProps> = ({ isOpen, onToggle }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const handleLogout = () => {
    dispatch(logout());
    navigate('/');
  };

  const menuItems = [
    {
      to: '/dealer/dashboard',
      label: 'Dashboard',
      icon: Home,
      description: 'Overview & Stats',
    },
    {
      to: '/dealer/listings',
      label: 'Listing',
      icon: Package,
      description: 'Manage Listings',
    },
    {
      to: '/dealer/listings/sold',
      label: 'Sold Listings',
      icon: TrendingUp,
      description: 'Sold Items',
    },
    {
      to: '/dealer/profile',
      label: 'Profile Manage',
      icon: User,
      description: 'Manage Profile',
    },
  ];

  return (
    <>
      {/* Mobile/Tablet overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden"
          onClick={onToggle}
        />
      )}

      {/* Sidebar */}
      <div className={`
        fixed lg:static inset-y-0 left-0 z-50 w-64 bg-white border-r border-gray-200 flex flex-col 
        transform transition-all duration-300 ease-in-out shadow-xl lg:shadow-none
        ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        ${sidebarCollapsed ? 'lg:w-23 xl:w-20' : 'lg:w-64'}
      `}>
        {/* Header Section */}
        <div className="p-4 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center shadow-lg">
                <img src={DiasporawiseLogo} alt="Logo" className="w-5 h-5" />
              </div>
              {!sidebarCollapsed && (
                <div>
                  <h1 className="text-base font-bold text-gray-900">
                    DIASPORA<span className="text-blue-600">WISE</span>
                  </h1>
                  <p className="text-sm font-semibold text-gray-500">Dealer Panel</p>
                </div>
              )}
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                className="hidden lg:flex p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-all duration-200"
              >
                {sidebarCollapsed ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronLeft className="w-3.5 h-3.5" />}
              </button>
              <button
                className="lg:hidden p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-all duration-200"
                onClick={onToggle}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 overflow-y-auto">
          <div className="space-y-4">
            <ul className="space-y-0.5">
              {menuItems.map((item) => {
                const IconComponent = item.icon;
                const isActive = location.pathname === item.to || 
                  (item.to.includes('listings') && location.pathname.startsWith('/dealer/listings') && 
                   (item.to.includes('sold') ? location.pathname === '/dealer/listings/sold' : 
                    !location.pathname.includes('/sold')));
                
                return (
                  <li key={item.to}>
                    <NavLink
                      to={item.to}
                      onClick={() => {
                        if (window.innerWidth < 1024) {
                          onToggle();
                        }
                      }}
                      className={`group flex items-center gap-2 px-2.5 py-2.5 rounded-lg transition-all duration-200 text-sm font-bold relative overflow-hidden
                        ${isActive ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg' : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'}`}
                    >
                      <div className={`
                        p-1.5 rounded-md transition-all duration-200
                        ${isActive ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-600 group-hover:bg-gray-200 group-hover:text-gray-700'}
                      `}>
                        <IconComponent className="w-3.5 h-3.5" />
                      </div>
                      {!sidebarCollapsed && (
                        <div className="flex-1 min-w-0">
                          <span className="block truncate font-bold">{item.label}</span>
                          <span className={`
                            block text-sm truncate font-semibold transition-colors duration-200
                            ${isActive ? 'text-blue-100' : 'text-gray-500'}
                          `}>
                            {item.description}
                          </span>
                        </div>
                      )}
                      {isActive && (
                        <div className="absolute right-1.5 w-1.5 h-1.5 bg-white rounded-full"></div>
                      )}
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          </div>
        </nav>

        {/* Logout Button */}
        <div className="p-4 border-t border-gray-100">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-2 px-2.5 py-2.5 text-red-600 hover:bg-red-50 rounded-lg transition-all duration-200 text-sm font-bold"
          >
            <LogOut className="w-3.5 h-3.5" />
            {!sidebarCollapsed && <span>Sign Out</span>}
          </button>
        </div>
      </div>
    </>
  );
};

export default DealerSidebar;

